import { Button } from '@/components/ui/button';
import { Check, ChevronDown, Mic, Speaker } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useTalkdownDeviceSelection } from './useTalkdownDeviceSelection';

type SidebarAudioButtonProps = {
  onTalkdownDone: (audio: ArrayBuffer, selectedDevices?: string[]) => Promise<void>;
  onTalkdownEnd?: (durationMs: number) => void;
  outputs?: { id: string; display: string }[];
  defaultValue?: string[];
};

export const SidebarAudioButton = ({ onTalkdownDone, onTalkdownEnd, outputs, defaultValue }: SidebarAudioButtonProps) => {
  const [active, setActive] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | undefined>();
  const isWatchfulOnly = useFeatureFlagEnabled('watchful-only');
  const startTimeRef = useRef<number | null>(null);
  const {
    selectedDevices,
    toggleDevice,
    selectAllDevices,
    clearAllDevices,
  } = useTalkdownDeviceSelection(outputs, defaultValue);

  const startRecording = useCallback(async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    setActive(true);
    startTimeRef.current = Date.now();
    const recorder = new MediaRecorder(stream);

    const chunks: Blob[] = [];

    recorder.ondataavailable = (event) => {
      chunks.push(event.data);
    };

    recorder.onstop = async () => {
      setActive(false);
      const blob = new Blob(chunks);
      try {
        await onTalkdownDone(
          await blob.arrayBuffer(),
          (outputs && outputs.length > 0) ? selectedDevices : undefined
        );
      } finally {
        if (startTimeRef.current) {
          const duration = Date.now() - startTimeRef.current;
          startTimeRef.current = null;
          onTalkdownEnd?.(duration);
        }
      }
    };
    setMediaRecorder(recorder);
    recorder.start();
  }, [onTalkdownDone, selectedDevices, outputs?.length]);

  const endRecording = useCallback(() => {
    if (!mediaRecorder) return;
    mediaRecorder.stop();
    setActive(false);
  }, [mediaRecorder, setActive, onTalkdownEnd]);

  return (
    <div className="flex flex-col w-full space-y-2">
      <Button
        variant={active ? 'destructive' : 'default'}
        className="w-full h-12"
        onClick={(e) => {
          e.stopPropagation();
        }}
        onMouseDown={startRecording}
        onMouseUp={endRecording}
      >
        <Mic className="mr-2 h-4 w-4" /> 
        {active ? 'Recording...' : 'Push to Talk'}
      </Button>

      {(outputs?.length ?? 0) > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className="w-full h-10 justify-between"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center">
                <Speaker className="mr-2 h-4 w-4" />
                <span className="text-xs">
                  {selectedDevices.length === outputs!.length 
                    ? 'All Speakers'
                    : `${selectedDevices.length}/${outputs!.length} Selected`
                  }
                </span>
              </div>
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[280px] p-0">
            <div className="flex items-center justify-between text-xs text-gray-400">
              <button
                className="flex-1 border-r p-2 font-semibold text-lime transition-colors hover:bg-lime/5"
                onClick={(e) => {
                  e.preventDefault();
                  selectAllDevices();
                }}
              >
                Select All
              </button>
              <button
                className="flex-1 p-2 font-semibold text-lime transition-colors hover:bg-lime/5"
                onClick={(e) => {
                  e.preventDefault();
                  clearAllDevices();
                }}
              >
                Clear All
              </button>
            </div>
            <div className="h-px bg-[#2E3835]"></div>
            <div className="max-h-48 overflow-y-auto">
              {outputs!.map((device) => (
                <DropdownMenuItem
                  key={device.id}
                  onClick={(e) => {
                    e.preventDefault();
                    toggleDevice(device.id);
                  }}
                  className="flex items-center justify-between"
                >
                  <span className="truncate text-xs">
                    {device.display || `Speaker ${device.id.slice(0, 5)}...`}
                  </span>
                  {selectedDevices.includes(device.id) && <Check className="ml-2 h-4 w-4" />}
                </DropdownMenuItem>
              ))}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
};
